import { yupResolver } from '@hookform/resolvers/yup';
import constate from 'constate';
import { set } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FieldErrors,
  FieldValues,
  useForm as useReactForm,
  UseFormSetError,
  UseFormTrigger,
} from 'react-hook-form';

import { clearSubmitIntent } from 'core/utils/event';
import { clearLastFocusTarget, focusFirstInvalidField } from 'core/utils/fieldFocus';

export interface IFocusRequest {
  // Limits the focus to these fields (a step validating its own fields); all errors when absent.
  names?: string[];
}

const useForm = ({ schema, defaultValues = {}, resetDefaultValues = false }) => {
  const {
    handleSubmit: handleFormSubmit,
    control,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    resetField,
    setValue,
    getValues,
    trigger: triggerField,
    watch,
    setError: setFieldError,
    getFieldState,
    clearErrors,
  } = useReactForm<any>({
    resolver: yupResolver(schema),
    defaultValues,
    reValidateMode: 'onChange',
    // RHF's default, kept explicit on purpose: every failed submit relies on it to focus the
    // first invalid field (the shared controls hand RHF their focusable element).
    shouldFocusError: true,
  });

  const namesToFocus = useRef<string[]>([]);

  // Set by a failed submit or focusing trigger, read by Form (and steps rendered without it) to
  // move the focus to the invalid field shown first. A new object each time, so it re-runs the
  // consumer's effect once per attempt and never on a plain errors change.
  const [focusRequest, setFocusRequest] = useState<IFocusRequest | null>(null);

  // Focus is the default for an error set by hand and for a whole-form trigger(): both mean the
  // user is blocked. A batch set in one tick (server errors in a loop) focuses once, on the field
  // shown first. trigger('field') keeps RHF's no-focus default, so re-validating on change never
  // moves the cursor. Pass `shouldFocus: false` / `true` to override either.
  const setError = useCallback<UseFormSetError<FieldValues>>(
    (name, error, options) => {
      setFieldError(name, error, { ...options, shouldFocus: false });

      if (false === options?.shouldFocus) {
        return;
      }

      namesToFocus.current.push(name);

      if (namesToFocus.current.length > 1) {
        return;
      }

      queueMicrotask(() => {
        const batchErrors = namesToFocus.current.reduce<FieldErrors>(
          (batch, fieldName) => set(batch, fieldName, getFieldState(fieldName).error),
          {}
        );

        namesToFocus.current = [];

        focusFirstInvalidField(batchErrors);
      });
    },
    [getFieldState, setFieldError]
  );

  const handleSubmit = useCallback<typeof handleFormSubmit>(
    (onValid, onInvalid) => async (event) => {
      let isInvalid = false;

      // Before RHF validates: what an earlier attempt focused is not this attempt's RHF focus.
      clearLastFocusTarget();

      await handleFormSubmit(onValid, async (formErrors, invalidEvent) => {
        isInvalid = true;

        await onInvalid?.(formErrors, invalidEvent);
      })(event);

      if (!isInvalid) {
        return;
      }

      // Raised once RHF has published the errors, so the consumer's effect reads them.
      setFocusRequest({});
      // A mouse submit no longer moves the focus to the button (keepFocusOnMouseDown), so when the
      // invalid field already had the focus no focusin comes to release the submit intent latch.
      clearSubmitIntent();
    },
    [handleFormSubmit]
  );

  const trigger = useCallback<UseFormTrigger<FieldValues>>(
    async (name, options) => {
      const shouldFocus = options?.shouldFocus ?? undefined === name;

      if (shouldFocus) {
        clearLastFocusTarget();
      }

      const isValid = await triggerField(name, { ...options, shouldFocus });

      if (!isValid && shouldFocus) {
        setFocusRequest({ names: undefined === name ? undefined : [name].flat() });
      }

      return isValid;
    },
    [triggerField]
  );

  useEffect(() => {
    if (resetDefaultValues && !!defaultValues) {
      const currentValues = getValues();

      const hasValuesChanged = Object.keys(defaultValues).some(
        (key) => currentValues[key] !== defaultValues[key]
      );

      if (hasValuesChanged) {
        reset(defaultValues, {
          keepDirty: true,
          keepDirtyValues: true,
          keepErrors: true,
        });
      }
    }
  }, [defaultValues, reset, resetDefaultValues, getValues]);

  return {
    handleSubmit,
    control,
    errors,
    reset,
    resetField,
    setValue,
    getValues,
    trigger,
    watch,
    defaultValues,
    dirtyFields,
    isSubmitting,
    setError,
    clearErrors,
    focusRequest,
  };
};

export const [FormProvider, useFormContext] = constate(useForm);
